/**
 * Sign-in intent and the one-time sign-in report on the host page.
 * @module lib/signIn
 */

// Mirrors server/src/services/loginIntent.js; the server validates again.
const INTENTS = ['login_wall', 'comments'];
const CONTEXT_KEY = '__milla_login_context__';

function normalizeLoginOptions(options) {
  if (!options || typeof options !== 'object' || !INTENTS.includes(options.intent)) return null;
  const context = options.intent === 'login_wall' && options.context && typeof options.context === 'object'
    ? Object.fromEntries(Object.entries(options.context).filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value)))
    : null;
  // A direct provider skips the AuthKit page; the server honours it only when
  // that provider is enabled (config.workos.directProviders).
  const provider = ['google', 'apple'].includes(options.provider) ? options.provider : null;
  // A wall sign-in opens AuthKit on sign-up unless the reader chose "Sign in".
  const screen = options.screen === 'sign-in' ? 'sign-in' : null;
  return { intent: options.intent, context, provider, screen };
}

// The post a wall sign-in started from survives the AuthKit redirect.
function rememberLoginContext(authState, loginIntent) {
  try {
    if (loginIntent?.context?.postId) window.sessionStorage?.setItem?.(CONTEXT_KEY, JSON.stringify({ state: authState, postId: String(loginIntent.context.postId) }));
    else window.sessionStorage?.removeItem?.(CONTEXT_KEY);
  } catch (_) {
    // ignore storage errors
  }
}

function takePostId() {
  try {
    const stored = JSON.parse(window.sessionStorage?.getItem?.(CONTEXT_KEY) || 'null');
    window.sessionStorage?.removeItem?.(CONTEXT_KEY);
    return typeof stored?.postId === 'string' && /^\d{1,15}$/.test(stored.postId) ? stored.postId : null;
  } catch (_) {
    return null;
  }
}

function gtag() {
  try {
    if (typeof window.gtag === 'function') window.gtag.apply(window, arguments);
    else if (Array.isArray(window.dataLayer)) window.dataLayer.push(arguments);
  } catch (_) {
    // analytics must never break sign-in
  }
}

// Called once per completed AuthKit exchange (popup message or redirect hash),
// never for refreshes or cross-tab tokens. One `login_success`, plus one
// `new_account` when the exchange created the account.
function reportSignIn(signIn, { emitter, analytics = true } = {}) {
  if (!signIn || typeof signIn !== 'object') return;
  const intent = INTENTS.includes(signIn.intent) ? signIn.intent : 'default';
  const isNewAccount = signIn.isNewAccount === true;
  const postId = intent === 'login_wall' ? takePostId() : null;
  emitter?.emit('login:success', { intent, isNewAccount });
  if (analytics === false) return;
  const params = { intent, ...(postId ? { post_id: postId } : {}) };
  gtag('event', 'login_success', params);
  if (isNewAccount) gtag('event', 'new_account', params);
}

module.exports = { INTENTS, normalizeLoginOptions, rememberLoginContext, reportSignIn };
